import type { Rule } from '@tunasong/schemas'
import type { FC } from 'react'
import { Alert } from '../primitives/alert.js'
import { Button } from '../primitives/button.js'
import { Switch } from '../primitives/switch.js'
import { Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow } from '../primitives/table.js'
import { formatLastFired, ruleState, summariseConditions } from './automation-summary.js'
import { describeRule } from './rule-sentence.js'
import styles from './automations-index.module.css'

export interface AutomationsIndexProps {
  rules: Rule[]
  /**
   * Newest firing time per rule id, derived from the persisted firing batches.
   * `Rule` has no `lastFiredAt` field — nothing ever wrote it, so it was removed.
   */
  lastFiredByRuleId?: Record<string, string>
  /** True while the firing-batch data behind `lastFiredByRuleId` has not loaded yet. Every
   * row's Last ran cell renders as loading rather than asserting the rule never fired --
   * an empty `lastFiredByRuleId` is indistinguishable from "no data yet" otherwise. */
  historyLoading?: boolean
  onToggle: (ruleId: string, enabled: boolean) => void
  onSelect?: (ruleId: string) => void
  /** Clears a loop-guard pause and turns the rule back on. Omitting it renders no button
   * at all -- the index must never offer a control it cannot act on. */
  onResume?: (ruleId: string) => void
  /** Injected so "last ran" is testable without mocking the clock. */
  now?: Date
  className?: string
}

/**
 * Row selection must not swallow interactions meant for a control inside the row.
 * Without this, Space on the enable Switch hits the row's key handler, whose
 * preventDefault suppresses the checkbox's own toggle — silently breaking it for
 * keyboard users — and selects the row as a side effect. The same applies to
 * clicking the Switch, which otherwise bubbles into the row's onClick.
 */
const isFromInteractiveChild = (event: { target: EventTarget | null; currentTarget: EventTarget | null }) =>
  event.target !== event.currentTarget &&
  event.target instanceof Element &&
  Boolean(event.target.closest('input, button, a, select, textarea, label, [role="switch"]'))

export const AutomationsIndex: FC<AutomationsIndexProps> = props => {
  const {
    rules,
    lastFiredByRuleId = {},
    historyLoading = false,
    onToggle,
    onSelect,
    onResume,
    now = new Date(),
    className,
  } = props

  if (rules.length === 0) {
    return <p className={className}>No automations yet. Rules you add to an entity show up here.</p>
  }

  return (
    <Table className={[styles.table, className].filter(Boolean).join(' ')}>
      <TableHead>
        <TableRow>
          <TableHeaderCell>Automation</TableHeaderCell>
          <TableHeaderCell>Matches</TableHeaderCell>
          <TableHeaderCell>Last ran</TableHeaderCell>
          <TableHeaderCell>Enabled</TableHeaderCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rules.map(rule => {
          const state = ruleState(rule)
          const sentence = describeRule(rule)
          return (
            <TableRow
              key={rule.id}
              tabIndex={onSelect ? 0 : undefined}
              aria-label={onSelect ? `View history for: ${sentence.actionLabel}` : undefined}
              onClick={
                onSelect
                  ? event => {
                      if (isFromInteractiveChild(event)) {
                        return
                      }
                      onSelect(rule.id)
                    }
                  : undefined
              }
              onKeyDown={
                onSelect
                  ? event => {
                      if (isFromInteractiveChild(event)) {
                        return
                      }
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        onSelect(rule.id)
                      }
                    }
                  : undefined
              }
            >
              <TableCell>
                {sentence.actionLabel}
                {state === 'paused' ? (
                  <Alert
                    severity="warning"
                    role="status"
                    aria-live="polite"
                    action={onResume ? <Button onClick={() => onResume(rule.id)}>Turn back on</Button> : undefined}
                  >
                    Paused to prevent a loop.
                  </Alert>
                ) : null}
              </TableCell>
              <TableCell>{summariseConditions(rule)}</TableCell>
              <TableCell>{historyLoading ? 'Loading…' : formatLastFired(lastFiredByRuleId[rule.id], now)}</TableCell>
              <TableCell>
                <Switch
                  checked={state === 'on'}
                  disabled={state === 'paused'}
                  onCheckedChange={checked => onToggle(rule.id, checked)}
                  aria-label={
                    state === 'paused'
                      ? `Paused to prevent a loop: ${sentence.actionLabel}`
                      : `${state === 'on' ? 'Disable' : 'Enable'}: ${sentence.actionLabel}`
                  }
                />
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}
